import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SEOHead from "../components/shared/SEOHead.jsx";
import ResponsiveImage from "../components/shared/ResponsiveImage.jsx";
import { getEventsForLang, getFeaturedLarpPages, excerpt } from "../utils/content.js";
import NewsletterEmbed from "../components/newsletter/NewsletterEmbed.jsx";
import SponsorLogos from "../components/shared/SponsorLogos.jsx";

const LARP_PATH = { de: "larp-erleben", en: "experience-larp" };
const SPENDEN_PATH = { de: "spenden", en: "donate" };
const ABOUT_PATH = { de: "ueber-uns", en: "about" };

function formatEventDate(dateStr, lang) {
  return new Date(dateStr).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Home({ lang }) {
  const { t } = useTranslation();
  const upcomingEvents = getEventsForLang(lang)
    .filter((e) => e.frontmatter.status === "upcoming")
    .slice(0, 2);
  const featuredLarpPages = getFeaturedLarpPages(lang, 3);

  return (
    <>
      <SEOHead lang={lang} path="/" title={t("home.heroTitle")} description={t("home.heroSubtitle")} />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "64px", padding: "40px 40px 96px" }}>
          <section style={{ position: "relative", borderRadius: "14px", overflow: "hidden" }}>
            <ResponsiveImage
              src="/assets/brand/start_hero.png"
              alt=""
              priority
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "linear-gradient(90deg, rgba(11,9,8,0.94) 0%, rgba(11,9,8,0.8) 42%, rgba(11,9,8,0.35) 100%)",
              }}
            />
            <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: "22px", padding: "72px 44px" }}>
              <p className="tl-eyebrow" style={{ margin: 0 }}>
                {t("home.heroEyebrow")}
              </p>
              <h1
                className="tl-hero-h1"
                style={{
                  fontFamily: "var(--font-serif)",
                  fontSize: "48px",
                  lineHeight: 1.06,
                  color: "var(--color-cream)",
                  maxWidth: "22ch",
                  margin: 0,
                }}
              >
                {t("home.heroHeadline")}
              </h1>
              <p style={{ fontSize: "17px", lineHeight: 1.7, color: "var(--color-cream-soft)", maxWidth: "54ch", margin: 0 }}>
                {t("home.heroBody")}
              </p>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "8px" }}>
                <Link to={`/${lang}/events`} className="tl-pill tl-pill-solid">
                  {t("home.heroCtaEvents")}
                </Link>
                <Link to={`/${lang}/${ABOUT_PATH[lang]}`} className="tl-pill tl-pill-outline">
                  {t("home.heroCtaUeber")}
                </Link>
              </div>
            </div>
          </section>

          <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
            {[
              { title: t("home.feature1Title"), body: t("home.feature1Body") },
              { title: t("home.feature2Title"), body: t("home.feature2Body") },
              { title: t("home.feature3Title"), body: t("home.feature3Body") },
            ].map((feature) => (
              <div key={feature.title} className="tl-card" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", color: "var(--color-cream)" }}>
                  {feature.title}
                </span>
                <span style={{ fontSize: "14px", lineHeight: 1.7, color: "var(--color-cream-soft)" }}>{feature.body}</span>
              </div>
            ))}
          </section>

          {upcomingEvents.length > 0 && (
            <section style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "24px", flexWrap: "wrap" }}>
                <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
                  {t("home.eventsTeaserTitle")}
                </h2>
                <Link to={`/${lang}/events`} style={{ fontSize: "14px", color: "var(--color-gold)" }}>
                  {t("home.allEventsLink")}
                </Link>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "24px" }}>
                {upcomingEvents.map((event) => {
                  const f = event.frontmatter;
                  return (
                    <Link
                      key={f.slug}
                      to={`/${lang}/events/${f.slug}`}
                      className="tl-card-light tl-hover-lift"
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                      <div
                        className="tl-diagonal-light"
                        style={{ height: "160px", display: "flex", alignItems: "flex-end", padding: "12px" }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "26px" }}>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                          <span className="tl-badge">{formatEventDate(f.date, lang)}</span>
                          <span style={{ fontSize: "12px", color: "rgba(10,9,7,0.7)" }}>{f.location}</span>
                        </div>
                        <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", lineHeight: 1.22, color: "var(--color-ink)" }}>
                          {f.title}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "6px" }}>
                          <span style={{ fontSize: "13px", color: "rgba(10,9,7,0.7)" }}>
                            {t("home.eventPriceFrom")} {f.price} €
                          </span>
                          <span className="tl-pill tl-pill-dark" style={{ fontSize: "13px" }}>
                            {t("home.eventDetailsCta")}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          <SponsorLogos lang={lang} />

          <section style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
              {t("nav.newsletter")}
            </h2>
            <div className="tl-card-light tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "40px" }}>
                <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "15px", color: "#8a6520" }}>
                  {t("home.newsletterEyebrow")}
                </span>
                <p style={{ fontFamily: "var(--font-serif)", fontSize: "28px", lineHeight: 1.18, color: "var(--color-ink)", maxWidth: "24ch", margin: 0 }}>
                  {t("home.newsletterTitle")}
                </p>
                <p style={{ fontSize: "15px", lineHeight: 1.7, color: "rgba(10,9,7,0.7)", maxWidth: "46ch", margin: 0 }}>
                  {t("home.newsletterBody")}
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px", background: "rgba(10,9,7,0.05)" }}>
                <NewsletterEmbed lang={lang} />
              </div>
            </div>
          </section>

          <section
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "40px",
              flexWrap: "wrap",
              border: "1px solid rgba(206,149,50,0.55)",
              borderRadius: "14px",
              padding: "36px 40px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", flex: "1 1 420px" }}>
              <p style={{ fontFamily: "var(--font-serif)", fontSize: "28px", lineHeight: 1.2, color: "var(--color-cream)", margin: 0 }}>
                {t("home.donateBannerTitle")} <span style={{ color: "var(--color-gold)" }}>{t("home.donateBannerTitleHighlight")}</span>
              </p>
              <p style={{ fontSize: "14px", lineHeight: 1.65, color: "var(--color-cream-soft)", maxWidth: "60ch", margin: 0 }}>
                {t("home.donateBannerBody")}
              </p>
            </div>
            <Link to={`/${lang}/${SPENDEN_PATH[lang]}`} className="tl-pill tl-pill-primary" style={{ padding: "16px 34px", fontSize: "16px" }}>
              {t("home.donateBannerCta")}
            </Link>
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxWidth: "56ch" }}>
              <span className="tl-eyebrow" style={{ fontSize: "15px" }}>
                {t("home.wissenEyebrow")}
              </span>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: "26px", lineHeight: 1.2, color: "var(--color-cream)" }}>
                {t("home.wissenTitle")}
              </span>
              <span style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)" }}>{t("home.wissenBody")}</span>
            </div>

            {featuredLarpPages.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px" }}>
                {featuredLarpPages.map((page) => (
                  <Link
                    key={page.fileSlug}
                    to={`/${lang}/${LARP_PATH[lang]}/${page.fileSlug}`}
                    className="tl-card-light tl-hover-lift"
                    style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "28px" }}
                  >
                    <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", lineHeight: 1.25, color: "var(--color-ink)" }}>
                      {page.frontmatter.title}
                    </span>
                    <span style={{ fontSize: "14px", lineHeight: 1.65, color: "rgba(10,9,7,0.7)" }}>
                      {page.frontmatter.subtitle || excerpt(page.content)}
                    </span>
                    <span style={{ fontSize: "13px", color: "var(--color-ink)", marginTop: "4px" }}>{t("larp.readMore")}</span>
                  </Link>
                ))}
              </div>
            )}

            <Link to={`/${lang}/${LARP_PATH[lang]}`} style={{ fontSize: "14px", color: "var(--color-gold)", alignSelf: "flex-start" }}>
              {t("home.wissenLink")}
            </Link>
          </section>
        </div>
      </div>
    </>
  );
}
